/**
 * When the app is allowed to have an opinion, and how it avoids repeating one.
 *
 * The diary's neutral voice states facts and stops. The sass layer is the
 * opt-in deviation: a Wednesday-Addams register that comments on what just
 * happened. This module holds everything about that layer which is not a joke —
 * which moments may speak, which of them must speak, and how a line is chosen
 * so that the same joke is not heard twice in a fortnight. The lines themselves
 * live with the app; a library of facts should not also be a library of jokes.
 *
 * Humour dies by repetition, so selection is the load-bearing part. Two rules
 * do the work: most log commits say nothing (scarcity keeps the voice a
 * surprise rather than a chore), and a chosen line is quarantined for
 * REPEAT_WINDOW_DAYS before it may be chosen again. The window is enforced
 * here, against a history the caller persists; the pool sizes that make the
 * window satisfiable are enforced by the catalogue's own tests.
 */

import type { GoalPhase } from './energy.js'
import type { FoodComedyClass } from './foodlore.js'
import type { MoodKey } from './mood.js'

export type SassEvent =
  | 'log_success'
  | 'entry_deleted'
  | 'workout_deleted'
  | 'recipe_deleted'
  | 'entry_edited'
  | 'entry_moved'
  | 'meal_repeated'
  | 'burn_logged'
  | 'water_milestone'
  | 'greeting'
  | 'login'
  | 'day_over'
  | 'day_under'
  | 'what_fits'
  | 'empty_today'
  | 'empty_trends'
  | 'weekly_budget_over'
  | 'weekly_budget_left'
  | 'streak'
  | 'protein_skew'
  | 'weight_trend'
  /**
   * The scale reported in and the row has just landed on this phone. Fires
   * from the sync engine, once per reading, only for today's — see
   * `glitime/scale.ts`. Her push already said the number on the lock screen;
   * this is what she says when he walks in.
   */
  | 'scale_spoke'
  | 'empty_list'
  | 'loading'
  // Her corner of the diary, opened on purpose rather than walked past. The
  // greeting is what she says to the room; these two are what she says when
  // somebody actually turns round and asks.
  | 'presence_remark'
  | 'herday_detail'
  // The interaction layer: the user talking back. A chip under one of her
  // toasts earns a comeback; holding her mark earns the petting reaction (a
  // denial, mostly). The chat_* events are the scripted edges of the LLM
  // conversation — the excuse when the line to the beyond is down, the "tired"
  // when the daily budget runs out, her own exit, and the sharp exit rudeness
  // buys. promise_broken fires at log time when an entry matches something the
  // user told her they would not do.
  | 'chip_comeback'
  | 'petted'
  | 'chat_excuse'
  | 'chat_tired'
  | 'chat_exit'
  | 'chat_rude_exit'
  /** He knocked while she was away — the closed door answers instead of her. */
  | 'chat_door'
  /** The first visit after a door she closed herself. */
  | 'chat_thaw'
  | 'promise_broken'
  // Ambient: the rare word she offers without being spoken to at all — see
  // PERSONA.md's "Netikėtos akimirkos". At most one of the two fires on any
  // given day, rationed by `glitime/ambient.ts` rather than by anything
  // here; this module only needs the two names to hang pools off.
  | 'ambient_stare'
  | 'ambient_kettle'
  // Errors, per PERSONA.md's reversed rule: full sass, and the fix always
  // present — either baked into the line itself, or carried by whatever
  // hint/button already sits next to it in the UI. One event per surface
  // rather than buckets on a shared one, matching how distinct as `day_over`
  // and `weekly_budget_over` already are for two numerically similar things:
  // the fix differs by surface, so the pool has to.
  | 'error_camera_denied'
  | 'error_camera'
  | 'error_mic_denied'
  | 'error_mic'
  | 'error_voice_empty'
  | 'error_voice_network'
  | 'error_offline'
  | 'error_sync'
  | 'error_save'
  | 'error_scan'
  | 'error_login_password'
  | 'error_login'
  | 'error_import'

/**
 * Everything a surface can tell the picker. All optional beyond the hour:
 * a call site passes what it knows and the templates use what they get.
 */
export interface SassContext {
  /** Local hour, 0–23. Drives dayparts and the midnight trigger. */
  hour: number
  phase?: GoalPhase | null
  /** Raw display name; templates apply vocativeLt themselves. */
  name?: string | null
  /** Whether lines about Saulius may be mixed in — false on his own account. */
  cameoOk?: boolean
  remainingKcal?: number
  kcalTarget?: number
  isOver?: boolean
  proteinOwedG?: number
  streakDays?: number
  weightTrendKgWk?: number | null
  /** The weigh-in the `scale_spoke` line is about. */
  weightKg?: number | null
  /** Its body fat, or null when the reading came through socks. */
  bodyFatPct?: number | null
  /** The deleted or edited entry's name — delete lines must repeat it back. */
  itemName?: string
  entryKcal?: number
  /** MealSlot as a plain string, so this module needs nothing from schemas. */
  slot?: string
  /** Snack entries already logged today, counted BEFORE this commit. */
  snackCountToday?: number
  /** Whether the day was already over budget BEFORE this commit. */
  wasOverBefore?: boolean
  waterMl?: number
  /** Batch size for voice logging, copied rows for repeat-yesterday. */
  count?: number
  overByKcal?: number
  perDayKcal?: number | null
  daysLeft?: number
  /**
   * Which screen/feature is asking, for the events that have no numeric
   * context to bucket on — `empty_list` and `loading`. A plain string rather
   * than a closed union: this module should not need to know every surface
   * the app has, only that callers agree on the same spelling per surface.
   */
  surface?: string
  /** Computed by `mood.ts`, attached ahead of time — never read live here. */
  mood?: MoodKey
  /**
   * What kind of thing was just logged, per `foodlore.ts`. Null when the name
   * matched nothing — she reacts to a category she recognises or says nothing
   * about the category at all, never a guess.
   */
  foodClass?: FoodComedyClass | null
  /** How many times this same thing has been logged in the trailing week, this one included. */
  repeatCountWeek?: number
  /**
   * Days the diary has been silent, for the escalating neglect register. 0
   * means something was logged today; the greeting surfaces pass it, the log
   * surfaces never need to (a log just landed).
   */
  daysSinceLastLog?: number
  /**
   * How many toasts have already fired in this burst — 0 for the first, 1 for
   * the second, and so on. Logging a dinner is three writes in twenty seconds,
   * and three lines that each pretend the other two didn't happen is the
   * clearest tell that nobody is actually listening on the other end.
   */
  burst?: number
  /**
   * Whether this line may say the food's name out loud. Decided at the call
   * site, because it needs both the name's length and a coin toss, and neither
   * belongs in a pure pool lookup. See `NAMED_LINE_RATE`.
   */
  namedOk?: boolean
  /**
   * What is going on in her own life today, per `herlife.ts` — a household
   * micro-event or the current chapter of a running bit. Null on most days.
   * Lore that is only ever asserted ("the appliances are my informants") and
   * never dramatised is set dressing; this is the mechanism that lets
   * something actually happen to her.
   */
  herLife?: string | null
  /**
   * How the interaction layer has left her feeling about this account today —
   * `warmed` after enough petting, `cooled` for the rest of the day after a
   * rude chat exit. Device-local and deliberately absent from the push side:
   * the lock screen never knows she was petted, and that asymmetry is honest
   * rather than a bug (see `glitime/interaction.ts`). Sits below every real
   * mood — a genuinely bad week is never upstaged by an apology owed.
   */
  affection?: 'warmed' | 'cooled'
  /**
   * Text the user just gave her — a promise being quoted back, mostly.
   * Templates that carry it must truncate; the lints measure them at a long
   * value.
   */
  promiseText?: string
  /** ISO date a remembered promise was declared on. */
  promiseDate?: string
}

/**
 * The length budgets every line is written against, enforced by the catalogue
 * lint at maximal context.
 *
 * The toast budget used to be 90, and 90 is part of why the catalogue drifted
 * into engraved epigrams: at that width the only sentence that fits is a
 * compressed one, so every line became a compressed one. 120 buys the room a
 * spoken sentence needs — a false start, a particle, a name — without letting
 * a toast become a paragraph.
 */
export const TOAST_MAX_CHARS = 120
export const SCREEN_MAX_CHARS = 160
/** The dry register stays clipped: that tightness is the whole point of it. */
export const DRY_TOAST_MAX_CHARS = 60
export const DRY_SCREEN_MAX_CHARS = 100

/** How often a log line that could name the food actually names it. */
export const NAMED_LINE_RATE = 1 / 3
/** Longer names are left out of named lines: the joke needs the sentence, not just the label. */
export const NAMED_LINE_MAX_CHARS = 24
/** Toasts closer together than this are one burst — one meal being entered, not three events. */
export const BURST_WINDOW_MS = 90_000

/** A log this size never passes unremarked. */
export const HUGE_PORTION_KCAL = 600
/** The snack that makes it a pattern. */
export const NTH_SNACK = 4
/** Logging in this half-open hour range is its own confession. */
export const MIDNIGHT_HOURS: readonly [number, number] = [0, 4]
/** How often an unremarkable log still gets a line. */
export const LOG_SASS_RATE = 2 / 3

/**
 * A line may not be heard twice within this window. Two weeks is the floor the
 * author asked for; raise it to 30 if the catalogue still wears thin.
 */
export const REPEAT_WINDOW_DAYS = 14
export const REPEAT_WINDOW_MS = REPEAT_WINDOW_DAYS * 24 * 60 * 60 * 1000

export type NotableReason =
  | 'tipped_over'
  | 'midnight'
  | 'repeat_food'
  | 'nth_snack'
  | 'huge_portion'

/**
 * The repeat counts worth remarking on — and only those.
 *
 * A threshold would be wrong here: "at least the fourth time this week" fires
 * on the fourth, the fifth, the sixth and the seventh, which turns someone's
 * ordinary daily breakfast into a forced toast every single morning. Two exact
 * counts instead: she notices the fourth, notices again at seven, and is
 * otherwise quiet about your habits.
 */
export const REPEAT_FOOD_NOTABLE: readonly number[] = [4, 7]

/**
 * The commits that always speak, in the order they win the microphone.
 *
 * Tipping the day over budget beats everything — it is the moment the ring
 * changes colour and silence would read as tact, which is not this voice.
 * Midnight beats quantity because when matters more than how much at 2 a.m.
 * A repeat beats both counting rules under it: "the fourth kebab this week" is
 * a fact about you, where a fourth snack is only a fact about today.
 */
export function notableReasons(ctx: SassContext): NotableReason[] {
  const reasons: NotableReason[] = []
  if (ctx.wasOverBefore === false && ctx.remainingKcal != null && ctx.remainingKcal < 0) {
    reasons.push('tipped_over')
  }
  if (ctx.hour >= MIDNIGHT_HOURS[0] && ctx.hour < MIDNIGHT_HOURS[1]) reasons.push('midnight')
  if (ctx.repeatCountWeek != null && REPEAT_FOOD_NOTABLE.includes(ctx.repeatCountWeek)) {
    reasons.push('repeat_food')
  }
  if (ctx.slot === 'snack' && (ctx.snackCountToday ?? 0) >= NTH_SNACK - 1) {
    reasons.push('nth_snack')
  }
  if ((ctx.entryKcal ?? 0) >= HUGE_PORTION_KCAL) reasons.push('huge_portion')
  return reasons
}

/** Whether this log commit gets a visible line at all. */
export function shouldSassLog(ctx: SassContext, rng: () => number): boolean {
  return notableReasons(ctx).length > 0 || rng() < LOG_SASS_RATE
}

export type Daypart = 'morning' | 'day' | 'evening' | 'night'

/**
 * The same four parts of the day the neutral greeting uses, plus a night that
 * wraps midnight — an empty diary at 23:00 and one at 09:00 are different
 * jokes, and the pools are bucketed accordingly.
 */
export function daypartOf(hour: number): Daypart {
  if (hour >= 22 || hour < 4) return 'night'
  if (hour < 11) return 'morning'
  if (hour < 18) return 'day'
  return 'evening'
}

/** An overage under this many kcal is a stumble; at or above it, a statement. */
export const DAY_OVER_BIG_KCAL = 200

/**
 * The sub-pool within an event, so each pool stays small and apt. Callers
 * compose the full key as `event.bucket` (optionally `.phase` on top).
 */
export function bucketFor(event: SassEvent, ctx: SassContext): string {
  switch (event) {
    case 'greeting':
    case 'login':
    case 'empty_today':
      return daypartOf(ctx.hour)
    case 'day_over': {
      const overBy = ctx.overByKcal ?? (ctx.remainingKcal != null ? -ctx.remainingKcal : 0)
      return overBy >= DAY_OVER_BIG_KCAL ? 'big' : 'small'
    }
    case 'log_success': {
      // Something notable still wins the microphone: a burst is a fact about
      // the last minute, tipping the day over budget is a fact about the day.
      const notable = notableReasons(ctx)[0]
      if (notable != null) return notable
      const burst = ctx.burst ?? 0
      if (burst >= 2) return 'burst3'
      if (burst === 1) return 'burst2'
      return ctx.namedOk === true ? 'named' : 'base'
    }
    case 'empty_list':
    case 'loading':
      return ctx.surface ?? 'base'
    case 'chip_comeback':
      // The chip id travels as `surface`, the same escape hatch `empty_list`
      // uses: this module should not need to know every chip the UI offers.
      return ctx.surface ?? 'base'
    default:
      return 'base'
  }
}

/** One remembered pick: which line, and when it was last shown. */
export interface PoolPick {
  i: number
  t: number
}

/**
 * Choose a line index: uniformly random among those the window has not
 * quarantined, and if the whole pool is quarantined — the pool is under-sized
 * for how often its event fires — the least-recently-heard line loses its
 * protection rather than the app going silent. Random-with-quarantine, not a
 * rota: a rota is itself a pattern, and patterns are the thing being avoided.
 *
 * `eligible`, when given, narrows which indices may ever be returned — a mood
 * or calendar tag saying "not this one, not today." It is a mask over the
 * full pool rather than a filtered copy: the history above is keyed by index
 * into the *original* pool, so an index has to keep meaning the same line
 * from one call to the next or the no-repeat memory silently starts pointing
 * at the wrong lines. `undefined` means everything is eligible, unchanged
 * from before this parameter existed. Ineligible indices are excluded from
 * the least-recently-heard fallback too — an empty eligible set returns -1
 * rather than a line the mask just said no to.
 */
export function pickIndex(
  poolSize: number,
  history: ReadonlyArray<PoolPick>,
  now: number,
  rng: () => number,
  eligible?: ReadonlyArray<boolean>,
): number {
  if (poolSize <= 0) return -1
  const allowed = (i: number) => eligible == null || eligible[i] === true

  // Latest sighting per index; duplicates in a persisted history are harmless.
  const lastSeen = new Map<number, number>()
  for (const pick of history) {
    if (pick.i < 0 || pick.i >= poolSize) continue
    const prev = lastSeen.get(pick.i)
    if (prev == null || pick.t > prev) lastSeen.set(pick.i, pick.t)
  }

  const candidates: number[] = []
  for (let i = 0; i < poolSize; i++) {
    if (!allowed(i)) continue
    const seen = lastSeen.get(i)
    if (seen == null || now - seen >= REPEAT_WINDOW_MS) candidates.push(i)
  }
  if (candidates.length > 0) {
    return candidates[Math.floor(rng() * candidates.length) % candidates.length]!
  }

  let oldest = -1
  let oldestAt = Number.POSITIVE_INFINITY
  for (let i = 0; i < poolSize; i++) {
    if (!allowed(i)) continue
    const seen = lastSeen.get(i) ?? Number.NEGATIVE_INFINITY
    if (seen < oldestAt) {
      oldestAt = seen
      oldest = i
    }
  }
  return oldest
}

/** Drop history entries the window no longer cares about. */
export function pruneHistory(history: ReadonlyArray<PoolPick>, now: number): PoolPick[] {
  return history.filter((pick) => now - pick.t < REPEAT_WINDOW_MS)
}

/**
 * Small, seedable RNG for tests; production passes Math.random. Determinism is
 * a test concern, not a product feature.
 */
export function mulberry32(seed: number): () => number {
  let state = seed >>> 0
  return () => {
    state = (state + 0x6d2b79f5) >>> 0
    let t = state
    t = Math.imul(t ^ (t >>> 15), t | 1)
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61)
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

/**
 * FNV-1a: fast, well-spread, not a security hash. Turns a string seed (a user
 * id, a "userId:isoWeek" pair) into the number `mulberry32` wants, so the same
 * input always lands on the same pick — a whim that changes every render
 * would just be noise, not a whim.
 */
export function hashSeed(text: string): number {
  let hash = 0x811c9dc5
  for (let i = 0; i < text.length; i++) {
    hash ^= text.charCodeAt(i)
    hash = Math.imul(hash, 0x01000193)
  }
  return hash >>> 0
}

/**
 * A stable shuffle of 0..n-1 from a seed — the push sender's rotation. The
 * server keeps no memory of what it said, so it walks a per-user permutation
 * by day index instead: every line fires once before any repeats, which makes
 * the repeat window exactly the pool size in days for a daily push.
 */
export function seededPermutation(n: number, seed: number): number[] {
  const rng = mulberry32(seed)
  const order = Array.from({ length: n }, (_, i) => i)
  for (let i = n - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1)) % (i + 1)
    ;[order[i], order[j]] = [order[j]!, order[i]!]
  }
  return order
}
